// src/components/ProjectRequiredModal.tsx

/**
 * Informational modal displayed when a project-dependent feature is accessed
 * without an active project selection.
 *
 * This component is responsible for:
 * - informing the user that a project must be selected first
 * - blocking access to project-scoped features until acknowledged
 * - delegating follow-up action handling to the parent component
 */

import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import Button from './common/ActionButton';

/**
 * Props accepted by the project-required modal.
 */
interface ProjectRequiredModalProps {
  /** Controls whether the modal is visible. */
  open: boolean;

  /**
   * Callback invoked when the user acknowledges the message.
   *
   * The parent component decides whether this simply closes the modal or also
   * triggers navigation to a project selection flow.
   */
  onSelectProject: () => void;
}

/**
 * Render the modal shown when a project selection is required.
 *
 * @param props - Component props.
 * @returns Project-required informational modal.
 */
export default function ProjectRequiredModal({
  open,
  onSelectProject,
}: ProjectRequiredModalProps) {
  return (
    <Dialog open={open}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3,
          minWidth: 320,
        }}
      >
        <DialogTitle
          sx={{
            width: '100%',
            textAlign: 'center',
            fontWeight: 700,
            pb: 1,
          }}
        >
          Select a project
        </DialogTitle>

        <DialogContent
          sx={{
            width: '100%',
            textAlign: 'center',
            pb: 2,
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            You must select a project before accessing this feature.
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={onSelectProject}
          >
            Got it
          </Button>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
